<script>
    import {
        Modal,
        ModalHeader,
        ModalBody,
        ModalFooter,
        Button,
        Table
    } from "sveltestrap";
    import {afterUpdate, onMount} from "svelte";
    import {eventBridge, portalConfiguration} from "../../stores.js";
    import {writable} from "svelte/store";
    import Fa from "svelte-fa";
    import {faTrash} from "@fortawesome/free-solid-svg-icons";
    import ConfirmDeleteTokenModal from "./ConfirmDeleteTokenModal.svelte";
    import TokenDetailsModal from "./TokenDetailsModal.svelte";
    import {faCircleQuestion} from "@fortawesome/free-regular-svg-icons";
    import ButtonLine from "../snippets/ButtonLine.svelte";
    import AddTokenModal from "./AddTokenModal.svelte";
    import Help from "../snippets/Help.svelte";

    const tokens = writable([]);
    let selectedToken = null;
    export let showModal = writable(false);

    let showTokenDetails = writable(false);
    let showConfirmDeleteToken = writable(false);
    let showAddToken = writable(false);
    let tokenRows;
    let tokenRowIndex;
    let csHasSentTokens = false
    const TOKEN_ROW_FOCUS_DELAY = 1000; // in millis
    const useTokenRowFocusDelay = writable(false);
    
    const toggle = () => ($showModal = !$showModal);

    function registerTokenListEvents() {
        $eventBridge.addEventListener('cs:AvailableSecurityTokenList', (event) => {
            const payload = JSON.parse(event.payload);
            const tokensFromPayload = payload.securityTokenList;
            tokens.set(tokensFromPayload);
            csHasSentTokens = true;
            tokenRowIndex = undefined;
        });

        $eventBridge.dispatchEvent('cs:getAvailableSecurityTokenList');
        $eventBridge.dispatchEvent('cs:subscribeCardEvent');
    }

    onMount(() => {
        registerTokenListEvents();
        tokenRows = document.getElementsByClassName("token-administration-modal-token-row");
    });

    afterUpdate(() => {
        if ($showModal && csHasSentTokens && tokenRows.length === $tokens.length) {
            if(!$useTokenRowFocusDelay) {
                focusTableRows();
            } else {
                setTimeout(focusTableRows,TOKEN_ROW_FOCUS_DELAY);
                $useTokenRowFocusDelay = false;
            }
            csHasSentTokens = false;
        }
    });
    function confirmDeleteToken(token) {
        selectedToken = token;
        $showConfirmDeleteToken = true;
    }

    function openTokenDetails(token) {
        selectedToken = token;
        $showTokenDetails = true;
    }

    function handleTokenListKeyboardOperations(event) {
        if(jQuery("#token-administration-add-token").is(':focus')) {
            if (event.key === "Enter") {
                document.getElementById("token-administration-add-token").click();
                return;
            }
        }
        
        if(!tokenRows || !Array.from(tokenRows).includes(document.activeElement)) {
            return;
        }

        if (event.key === "ArrowUp") {
            tokenRowIndex = (--tokenRowIndex + tokenRows.length) % tokenRows.length;
            processTokenRowIndexUpdate();
        }

        if (event.key === "ArrowDown") {
            tokenRowIndex = ++tokenRowIndex % tokenRows.length;
            processTokenRowIndexUpdate();
        }

        if (event.key === "Enter") {
            openTokenDetails($tokens[tokenRowIndex]);
        }

        if (event.key === "Delete") {
            tokenRows[tokenRowIndex].getElementsByClassName('delete-token')[0]?.click();
        }
    }

    function updateSelectedToken() {
        selectedToken = $tokens[tokenRowIndex];
    }
    
    function focusSelectedTokenRow() {
        tokenRows[tokenRowIndex].focus();
    }
    
    function processTokenRowIndexUpdate() {
        updateSelectedToken();
        focusSelectedTokenRow();
    }
    
    function focusAddTokenButton() {
        if(!$showModal) {
            return;
        }
        jQuery('#token-administration-add-token').focus();
    }

    function focusTableRows() {
        if (tokenRows.length === 0) {
            focusAddTokenButton();
            return;
        }
        if (tokenRowIndex === undefined) {
            tokenRowIndex = 0;
        }
        processTokenRowIndexUpdate();
    }
</script>
<svelte:window on:keydown={handleTokenListKeyboardOperations}/>

<Modal
    id="token-administration-modal"
    backdrop="static"
    isOpen={$showModal} 
    {toggle} 
    size="xl"
    scrollable
    on:open={focusTableRows}
>
    <ModalHeader {toggle}>Zertifikate verwalten</ModalHeader>

    <ModalBody>
        <Table hover style="table-layout: fixed;">
            <thead>
                <tr>
                    <th style="width: 30px;"></th>
                    <th>Technische Bezeichnung</th>
                    <th style="width: 20%;">Bezeichnung in beA</th>
                    <th style="width: 15%;">Art</th>
                    <th style="width: 15%;">Ablaufdatum</th>
                </tr>
            </thead>

            <tbody>
                {#each $tokens as token}
                    <tr on:click={() => {
                            tokenRowIndex = $tokens.indexOf(token);
                            processTokenRowIndexUpdate();
                        }}
                        on:dblclick={() => openTokenDetails(token)}
                        data-test-entity-id="{token.fingerprint}"
                        data-test-id="tokenAdministrationModal-dataTable-row-{token.fingerprint}"
                        class:unavailable={token.state === 'NOT_AVAILABLE'}
                        class:error={token.state.startsWith('ERROR')}
                        class:selected={token.fingerprint === selectedToken?.fingerprint}
                        class="token-administration-modal-token-row"
                        tabindex={token.fingerprint === selectedToken?.fingerprint ? '0' : '-1'}
                    >
                        {#if  token.type === 'SW'}
                            <td style="text-align: center;"
                                on:click={() => confirmDeleteToken(token)}
                                title="Zertifikat löschen"
                                class="delete-token"
                                data-test-id="tokenAdministrationModal-dataTable-row-action-delete-{token.fingerprint}"
                            >
                                <Fa icon="{faTrash}"/>
                            </td>
                        {:else}
                            <td />
                        {/if}
                        <td class="ellipsis" style="max-width: 40%;">{token.customLabel}</td>
                        <td>{token.customName || ''}</td>
                        <td>{token.type === 'HW' ? 'Hardware-Zertifikat' : 'Software-Zertifikat'}</td>
                        <td>{token.expiryDate}</td>
                    </tr>
                {/each}
            </tbody>
        </Table>
    </ModalBody>

    <ModalFooter>
        <ButtonLine>
            <Help slot="left" url="{$portalConfiguration.help ?? ''}/einstellungen-in-ihrem-bea/profilverwaltung/sicherheits-token/hinzufuegen" data-test-id="tokenAdministrationModal-help" />
            <a id="token-administration-add-token" tabindex="0" slot="middle" on:click={() => $showAddToken = true} data-test-id="tokenAdministrationModal-newToken">+ Neues Software-Zertifikat hinzufügen</a>
            <Button default on:click={() => $showTokenDetails = true} disabled={!selectedToken} color="primary" data-test-id="tokenAdministrationModal-button-details">Details anzeigen</Button>
        </ButtonLine>
    </ModalFooter>
</Modal>

<TokenDetailsModal showModal={showTokenDetails} token={selectedToken} />
<ConfirmDeleteTokenModal
    showConfirm={showConfirmDeleteToken}
    token={selectedToken}
    on:tokenDeletion={() => {
        selectedToken = null;
        $useTokenRowFocusDelay = true;
    }}
/>
<AddTokenModal showModal={showAddToken} on:submit={() => $useTokenRowFocusDelay = true} />

<style>
    thead {
        background-color: var(--brak-dark-blue);
        color: var(--brak-greyed-white);
    }

    tbody {
        cursor: pointer;
        user-select: none;
    }

    a {
        text-decoration: none;
        font-style: italic;
        font-weight: bold;
        color: var(--brak-dark-blue);
    }

    .delete-token > :global(*) {
        visibility: hidden;
    }

    tr:hover .delete-token > :global(*) {
        visibility: inherit;
    }

    .unavailable {
        background-color: darkgrey;
    }

    .error {
        background-color: var(--brak-red);
    }

    .selected {
        background-color: var(--brak-medium-blue);
    }
</style>